import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { siteInfo } from "@/mocks/site";

const BASE = "https://mynami.cn";
const FORM_URL = "https://readdy.ai/api/form/daih19rp14a1h3mr7450";
const MAX_LEN = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setFormError("");
      setStatus("success");
      form.reset();
      setMessage("");
      return;
    }
    formData.delete("company_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const lowered = String(serverMsg).toLowerCase();
      const code = parsed?.code;

      if (response.ok && code === "OK" && !lowered.includes("spam")) {
        setStatus("success");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(
          String(serverMsg) || "提交失败，请检查填写内容后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时无法提交，请稍后再试。");
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们",
      url: `${BASE}/contact`,
      description: "通过表单联系黑料每日编辑组，提交选题建议、内容纠错、投稿报料与商务合作。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "联系我们", item: `${BASE}/contact` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="联系我们 | 黑料每日选题建议与商务合作"
        description="联系黑料每日编辑组：提交选题建议、内容纠错、投稿报料或商务合作需求，填写表单后我们会在三个工作日内回复。"
        keywords="黑料每日联系,投稿报料,选题建议,商务合作,内容纠错"
        canonicalPath="/contact"
        jsonLd={jsonLd}
      />
      <div className="px-4 md:px-8">
        <Breadcrumbs items={[{ label: "联系我们" }]} />
      </div>

      <section className="px-4 md:px-8 pb-6 border-b border-background-200">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.2em] text-accent-700 uppercase mb-2">
          <span className="w-6 h-px bg-accent-500" />
          CONTACT
        </span>
        <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-3">联系我们</h1>
        <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          选题建议、内容纠错、投稿报料与商务合作，都可以通过下面的表单告诉我们。留清楚联系方式，编辑组会在三个工作日内回复。
        </p>
      </section>

      <div className="px-4 md:px-8 py-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <form
            data-readdy-form
            id="contact-inquiry-form"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                  你的称呼 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  required
                  placeholder="例如：林澈"
                  className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                  联系邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">
                咨询类型 <span className="text-primary-500">*</span>
              </label>
              <select
                id="contact-topic"
                name="topic"
                required
                defaultValue=""
                className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="" disabled>
                  请选择咨询类型
                </option>
                <option value="选题建议">选题建议</option>
                <option value="内容纠错">内容纠错</option>
                <option value="投稿报料">投稿报料</option>
                <option value="商务合作">商务合作</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                具体说明 <span className="text-primary-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={6}
                maxLength={MAX_LEN}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="请尽量写清楚背景、时间线与相关链接，方便我们快速跟进。"
                className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              />
              <span className="text-xs text-foreground-400 text-right">
                {message.length} / {MAX_LEN}
              </span>
            </div>

            <div className="company_alt_hidden">
              <label htmlFor="contact-company-alt">公司名称（选填）</label>
              <input
                id="contact-company-alt"
                name="company_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 px-6 py-3 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true" />
                {status === "submitting" ? "提交中…" : "提交信息"}
              </button>
              <p className="text-xs text-foreground-500">我们只会将你的信息用于回复本次咨询。</p>
            </div>

            {status === "success" && (
              <div className="mt-5 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3" role="status">
                <i className="ri-checkbox-circle-line text-secondary-600 text-lg" aria-hidden="true" />
                <p className="text-sm text-secondary-900">
                  已收到你的信息，感谢留言！编辑组会在三个工作日内回复。
                </p>
              </div>
            )}
            {status === "error" && (
              <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 px-4 py-3" role="alert">
                <i className="ri-error-warning-line text-primary-600 text-lg" aria-hidden="true" />
                <p className="text-sm text-primary-800">{formError}</p>
              </div>
            )}
          </form>
        </div>

        <aside className="lg:col-span-1 flex flex-col gap-6" aria-label="其他联系方式">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">其他联系方式</h2>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">合作咨询</span>
                  <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-900 hover:text-primary-600 transition-colors break-all">
                    {siteInfo.email}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 shrink-0">
                  <i className="ri-send-plane-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">报料投稿</span>
                  <a href={`mailto:${siteInfo.tipEmail}`} className="text-sm text-foreground-900 hover:text-primary-600 transition-colors break-all">
                    {siteInfo.tipEmail}
                  </a>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                  <i className="ri-time-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">回复时间</span>
                  <span className="text-sm text-foreground-900">三个工作日内</span>
                </span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">先看看常见问题</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              关于更新频率、内容筛选与转载规则，常见问题页面已经有详细说明。
            </p>
            <Link
              to="/faq"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              前往常见问题
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}